"use client";

import { useEffect } from "react";
import {
  clerkFrontendApiHost,
  clerkJsScriptUrl,
  isClerkEntryHref,
} from "@/lib/clerk-warmup";

/**
 * Warms Clerk for an anonymous visitor heading to /signup, /login or
 * /start-project from a Clerk-free page.
 *
 * Since #3408 those pages load clerk-js from cold on arrival, and the sign-up
 * form stays empty until it has (about a second on broadband, several on site
 * 4G). Completion fell from ~70% to ~15% the day that shipped, with visitors
 * leaving /signup before touching the form.
 *
 * On pointer, touch or keyboard intent on such a link (hover, touchstart or
 * focus all come before the click), it preconnects to the Frontend API and
 * preloads the pinned clerk-js; the provider on the destination then runs the
 * cached copy. Nothing executes on this page. (Not rel=prefetch: CSP checks
 * that against default-src, which is 'self' only; a script preload is checked
 * against script-src, which allows Clerk.)
 *
 * Intent only, never on view: anonymous visitors who do not head for a Clerk
 * page still load nothing from Clerk (marketing-clerk-free.spec.ts, Alec's
 * 26 Oct decision). No-op where Clerk is already loaded (signed-in upkeep,
 * app pages) and when the visitor has asked the browser to save data.
 */

function addHint(rel: "preconnect" | "preload", href: string): void {
  if (document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  // ClerkProvider loads the script with crossorigin="anonymous"; the hint
  // must match or the browser fetches it twice.
  link.crossOrigin = "anonymous";
  if (rel === "preload") link.setAttribute("as", "script");
  link.dataset.clerkWarmup = rel;
  document.head.appendChild(link);
}

function saveDataRequested(): boolean {
  const connection = (navigator as Navigator & {
    connection?: { saveData?: boolean };
  }).connection;
  return connection?.saveData === true;
}

function closestEntryLink(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const anchor = target.closest("a[href]");
  return (
    anchor instanceof HTMLAnchorElement &&
    isClerkEntryHref(anchor.href, window.location.origin)
  );
}

export function ClerkIntentWarmup() {
  useEffect(() => {
    const host = clerkFrontendApiHost(
      process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
    );
    if (!host || saveDataRequested()) return;
    const scriptUrl = clerkJsScriptUrl(host);
    const clerkLoaded = () =>
      typeof (window as Window & { Clerk?: unknown }).Clerk !== "undefined";

    let warmed = false;
    const onIntent = (event: Event) => {
      if (warmed || clerkLoaded() || !closestEntryLink(event.target)) return;
      warmed = true;
      addHint("preconnect", `https://${host}`);
      addHint("preload", scriptUrl);
    };
    const intentEvents = ["pointerover", "touchstart", "focusin"] as const;
    for (const type of intentEvents) {
      document.addEventListener(type, onIntent, { passive: true, capture: true });
    }
    return () => {
      for (const type of intentEvents) {
        document.removeEventListener(type, onIntent, { capture: true });
      }
    };
  }, []);

  return null;
}
